import React, { useEffect, useRef, useState } from "react";
import * as d3Force from "d3-force";
import { Link } from "react-router-dom";
import { debounce } from "debounce";
import { map, clamp } from "../../utils.js";

import { FilterValue } from '../filter';
import allArticles from "./../../content";

let d3Nodes = [];
let d3Links = [];
let width = window.innerWidth;
let height = window.innerHeight;
let simulation;
const spaceX = 100;

const articleCards = allArticles;

const Simulation = ({ filter }) => {
  const simulationWrapper = useRef(0);
  const [ nodes, setNodes ] = useState([]);
  const [ hoverCategory, setHoverCategory ] = useState([]);
	const [ { x, y, w, h }, setSavePositions ] = useState({
    x: 0,
    y: 0,
    w: 0,
    h: 0,
  });

	// let x = 0;
	// let y = 0;
	// let w = 0;
	// let h = 0;

	// if(location.state) {
	// 	const { card } = location.state
	// 	x = card.savePosition.x;
	// 	y = card.savePosition.y;
	// 	w = card.savePosition.w;
	// 	h = card.savePosition.h;
	// }


  //prepare refs for all nodes
  const refs = [];
  for (let i = 0; i < articleCards.length * 3; i++) {
    refs.push(useRef());
  }

	const handleResize = () => {
		setForces();
	}

	const setForces = () => {
		simulation.stop();
		let wRatio = window.innerWidth / window.innerHeight;
		simulation
      .force("charge", d3Force.forceManyBody().strength(clamp(map(window.innerWidth, 1090, 1440, -200, -600), -600, -200))) //between brekpoints 1090 and 1440 force is scaled down relative to width
      .force("forceX", d3Force.forceX(window.innerWidth / 2).strength(0.1))
      .force("forceY", d3Force.forceY(window.innerHeight / 2).strength(0.1 * wRatio)) //forceY is dependet on the aspect ratio of the window
		simulation.alpha(1).restart();
	}

	useEffect(() => {
		let resizeHandler = null
		window.addEventListener('resize', resizeHandler = () => debounce(handleResize(), 200), false)
		return () => {
			window.removeEventListener('resize', resizeHandler)
		}
	}, [])

  useEffect(() => {
    d3Nodes = []; //when initalized reset array
    d3Links = []; //when initalized reset array

    createNodesFromArray(articleCards);
    linkNodes();

    simulation = d3Force.forceSimulation(d3Nodes);
    simulation.on("tick", draw);
		setForces();
    //simulation.force("links", d3Force.forceLink(d3Links).distance(10))
    simulation.force(
        "links",
        d3Force.forceLink(d3Links).distance(function (d) {
          let dist = 50;
          if (d.source.ref.current) {
            dist = d.source.ref.current.offsetWidth / 2;
          }
          return dist;
        })
      )
      .force("collison", d3Force.forceCollide(32)); //prevent collision / set radius of nodes

    setNodes(d3Nodes);

    return () => {
      simulation.stop(); //stop simulation when component is unmounted
    };
  }, []);

  useEffect(() => {
    if (filter == FilterValue.NONE) {
      filterNodes(filter);
			return
    }
		filterNodes(filter, articleCards.filter(a => a.meta.category === filter));
  }, [filter]);

  const createNode = (i, x, y, tag = { slug: "empty" }) => {
    d3Nodes.push({
      id: i,
      x: x,
      y: y,
      type: "parent",
      content: tag,
      ref: refs[i * 3],
    });
    d3Nodes.push({
      //creating a spacer node which follows its parent
      id: i + 0.1,
      parentIndex: i * 3,
      x: d3Nodes[i * 3].x - spaceX / 2, //allow force on x of spacer –> but i t should be linked then
      get fy() {
        return d3Nodes[this.parentIndex].y;
      },
      type: "spacer",
      content: tag,
      ref: refs[i * 3 + 1],
    });
    d3Nodes.push({
      //creating a spacer node which follows its parent
      id: i + 0.2,
      parentIndex: i * 3,
      x: d3Nodes[i * 3].x + spaceX / 2,
      get fy() {
        return d3Nodes[this.parentIndex].y;
      },
      type: "spacer",
      content: tag,
      ref: refs[i * 3 + 2],
    });
  };

  const createNodesFromArray = (array) => {
    array.forEach((tag, i) => {
      let randomX = Math.random() * width;
      let randomY = Math.random() * height;
      createNode(i, randomX, randomY, tag.meta);
    });
  };

  const linkNodes = () => {
    d3Links = [];
    d3Nodes.forEach((node, i) => {
      if (i % 3 == 0) {
        d3Links.push(
          { source: i, target: i + 1 },
          { source: i, target: i + 2 }
        );
      }
    });
  };

  const updateParents = (array) => {
    //if the amount of nodes is changed refrenced parentIndex in spacer nodes must be updated
    let saveParentI = false;
    array.forEach((el, i) => {
      if (el.type == "parent") {
        saveParentI = i;
      } else {
        el.parentIndex = saveParentI;
      }
    });
  };

  const draw = () => {
    d3Nodes.forEach((node) => {
      if (node.ref)
        node.ref.current.style = `transform: translate3d(calc(${node.x}px - 50%),${node.y}px,0)`;
    });
  };

  const filterNodes = (filterValue, sourceArray = articleCards) => {
		if (d3Nodes && simulation != null) {
      let filteredNodes = d3Nodes.filter(
        (el) => el.content.category == filterValue
      ); //filter with category
      d3Nodes = filteredNodes; //apply filter to original array
      if (d3Nodes.length <= 0) {
        //if filtered array is empty, create new nodes
        createNodesFromArray(sourceArray);
      }
      updateParents(d3Nodes); //update references in spacer nodes
      simulation.nodes(d3Nodes); //apply nodes to simulation
      linkNodes(); //update links array
      //reset links:
      simulation.force(
        "links",
        d3Force.forceLink(d3Links).distance(function (d) {
          let dist = 50;
          if (d.source.ref.current) {
            dist = d.source.ref.current.offsetWidth / 2;
          }
          return dist;
        })
      );
      simulation.alpha(1).restart(); //restart animation
      setNodes([...d3Nodes]); //update state to rerender only existing nodes
    }
  };

	const hoverGroup = (category, direction) => {
		direction === 'leave' ? setHoverCategory(false) : setHoverCategory(category)
	}

	const savePositions = (node) => {
    const target =  node.ref.current.querySelector(".nodeImage img");
    const boundingRect = target.getBoundingClientRect();
    setSavePositions({
      x: boundingRect.left,
      y: boundingRect.top,
      w: target.offsetWidth,
      h: target.offsetHeight,
    });
  };

  return (
		<div ref={simulationWrapper} className={`simulationWrapper hover-${hoverCategory}`}>
			{nodes.map((node) => (
				<Link
					onMouseDown={() => savePositions(node) }
					ref={node.ref}
					to={{
						pathname: `/${node.content.slug}`,
						state: {
							card: { savePosition: { x: x, y: y, w: w, h: h } }
						}
					}}
					className={
						node.type == "spacer"
							? "node--spacer"
							: `node node--${node.content.category}`
					}
					key={`node-link-${node.id}`}
					onMouseEnter={ node.type !== "spacer" ? () => hoverGroup(node.content.category, 'enter') : null }
					onMouseLeave={ node.type !== "spacer" ? () => hoverGroup(node.content.category, 'leave') : null }
				>
					<div className="nodeImage">
						<img src={`assets/cover/${node.content.slug}.jpg`}/>
					</div>
					<div className="nodeBubble" key={`node-${node.id}`}>
						{node.content ? `#${node.content.name}` : "empty"}
					</div>
				</Link>
			))}
		</div>
  );
};

export default Simulation;
